import {MouseEvent, useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {toast} from "react-toastify";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import moment from "moment/moment";
import {parseNumberWithCommaDecimal} from "app/utils/stringUtil";
import {ChartUtil} from "app/utils/chartUtil";
import {Mdb021Service} from "app/services/mdb/mdb021Service";
import {ResponseResultCode} from "app/model/response-api";
import DashboardCard from "./DashboardCard";
import {DashboardModuleProps} from "../../../model/db/dashboardModuleProps";

const defaultChartSeries: Highcharts.SeriesOptionsType[] = [
  {
    type: 'column',
    name: '사용 요금',
    data: [],
    color: {
      linearGradient: {x1: 0, y1: 0, x2: 0, y2: 1},
      stops: [
        [0, 'rgba(123,198,174,1)'],
        [1, 'rgba(103,164,210,1)'],
      ],
    },
  },
  {
    type: 'line',
    name: '전력 사용량',
    data: [[]],
    color: '#E2A215',
    marker: {
      lineColor: '#E2A215',
    },
    yAxis: 1,
  },
  {
    type: 'line',
    name: "작년 동시간 전력 사용량",
    data: [[]],
    color: '#A9A9A9',
    marker: {
      lineColor: '#A9A9A9',
    },
    yAxis: 1,
  },
];

const options: Highcharts.Options = {
  chart: {
    type: 'column',
    height: 277,
    spacingTop: 0,
    spacingBottom: 0,
    marginTop: 80,
  },
  title: { text: '' },
  exporting: { enabled: false },
  credits: { enabled: false },
  legend: {
    align: 'right',
    verticalAlign: 'top',
    layout: 'horizontal',
    symbolHeight: 10,
    padding: 10,
    itemStyle: {
      fontSize: '0.75rem',
      color: '#444444',
    },
  },
  plotOptions: {
    column: {
      borderWidth: 0,
      pointWidth: 20,
      borderRadius: {
        radius: 5,
        scope: 'point',
        where: 'end',
      },
    },
    line: {
      dashStyle: 'Solid',
      lineWidth: 1.5,
      marker: {
        symbol: 'circle',
        lineWidth: 1.5,
        fillColor: '#fff'
      }
    },
  },
  tooltip: {
    formatter: function (this: any) {
    let tooltip = `<span><strong style="font-size: 0.75rem;">${this.x}</strong></span><br/>`;
    this.points.forEach((p: any, i: number) => {
      let s = 2;
      if (i === 0 || i === 1) {
        s = -1;
      }
      let r = Math.pow(10, s);
      let unit = '';
      if (p.series.name === '요금') {
        unit = ' 원';
      } else {
        unit = ' kWh';
      }
      tooltip += `
        <span style="color: ${p.series.color};">●&nbsp;</span>
        ${p.series.name}: 
        <strong style="font-size: 0.75rem;">
          ${parseNumberWithCommaDecimal(Math.round(p.y * r) / r)}
        </strong>${unit}<br/>
      `;
    });

    return tooltip;
  },
    style: {
      fontSize: '0.75rem',
    }
  },
  xAxis: {
    categories: [],
    title: undefined,
    min: 0,
    crosshair: true,
    lineColor: '#999',
    lineWidth: 1,
    labels: {
      style: {
        color: '#444',
        fontSize: '0.7rem',
      },
    },
  },
  yAxis: [
    {
      title: {
        text: '요금',
        align: 'high',
        rotation: 0,
        offset: 0,
        y: -25,
        style: {
          color: '#444',
          fontSize: '0.65rem',
        },
      },
      min: 0,
      softMax: 10,
      lineColor: '#999',
      lineWidth: 1,
      gridLineColor: '#CCC',
      gridLineWidth: 1,
      gridLineDashStyle: 'ShortDash',
      labels: {
        x: -5,
        y: 4,
        style: {
          color: '#444',
          fontSize: '0.7rem',
        },
        formatter: function (this: any) {
          return parseNumberWithCommaDecimal(this.value);
        },
      },
    },
    {
      opposite: true,
      title: {
        text: '사용량',
        align: 'high',
        rotation: 0,
        offset: 0,
        y: -25,
        style: {
          color: '#444',
          fontSize: '0.65rem',
        },
      },
      min: 0,
      softMax: 10,
      lineColor: '#999',
      lineWidth: 1,
      gridLineColor: '#CCC',
      gridLineWidth: 1,
      gridLineDashStyle: 'ShortDash',
      labels: {
        x: 5,
        y: 4,
        style: {
          color: '#444',
          fontSize: '0.7rem',
        },
        formatter: function (this: any) {
          return parseNumberWithCommaDecimal(this.value);
        },
      },
    },
  ],
  series: defaultChartSeries,
  responsive: {
    rules: [
      {
        condition: {
          maxWidth: 500,
        },
        chartOptions: {
          chart: {
            spacingTop: 20,
            marginTop: 20,
          },
          xAxis: {
            labels: {
              style: {
                fontSize: '0.5rem',
              },
            },
          },
          yAxis: [
            {
              title: {
                y: -10,
                style: {
                  fontSize: '0.5rem',
                },
              },
              labels: {
                style: {
                  fontSize: '0.5rem',
                },
              },
            },
            {
              title: {
                y: -10,
                style: {
                  fontSize: '0.5rem',
                },
              },
              labels: {
                style: {
                  fontSize: '0.5rem',
                },
              },
            },
          ],
          legend: {
            align: 'center',
            verticalAlign: 'bottom',
            layout: 'horizontal',
            itemStyle: {
              fontSize: '0.6rem',
            },
          },
          tooltip: {
            style: {
              fontSize: '0.6rem',
            }
          },
          plotOptions: {
            column: {
              pointWidth: undefined,
            },
            line: {
              lineWidth: 1,
              marker: {
                lineWidth: 1,
                width: 2,
                height: 2,
                radius: 2
              },
            },
          },
        },
      },
    ],
  },
};

export default function ElecUsageFee(props: Readonly<DashboardModuleProps>) {
  const {t} = useTranslation();
  const [chartOption, setChartOption] = useState<any>(options);
  const chartCategories = [...ChartUtil.monthToNumberArray].map(m => `${m}월`)
  const navigate = useNavigate();
  const search = {
    searchType: 'MONTH',
    fromDt: moment().format('YYYYMMDD') + '000000',
    toDt: moment().format('YYYYMMDD') + '235959'
  };

  useEffect(() => {
    handleClickSearchBtn();
  }, []);

  function handleClickSearchBtn() {
    Mdb021Service.getInstance().getChartData(search).then(response => {
      if (response && ResponseResultCode.Y === response.resultCode) {
        setChartOption({
          ...options,
          xAxis: {
            categories: ChartUtil.monthToNumberArray.map(m => `${m}월`),
          },
          series: convertToSeries(response.data),
        });
      } else {
        toast.error(t('common.message.error.data'));
      }
    })
    .catch(() => toast.error(t('common.message.error.data')));
  }

  function convertToSeries(data: any[]) {
    let newChart = defaultChartSeries as any;

    newChart[0].name = `요금`;
    newChart[1].name = `사용량`;
    newChart[2].name = `전년동월 사용량`;

    newChart[0].data = ChartUtil.correctMissingDataByCategory("MONTH", data, 'dt', 'pwrUsageCost', chartCategories);
    newChart[1].data = ChartUtil.correctMissingDataByCategory("MONTH", data, 'dt', 'pwrUsage', chartCategories);
    newChart[2].data = ChartUtil.correctMissingDataByCategory("MONTH", data, 'dt', 'prevPwrUsage', chartCategories);
    return newChart;
  }

  function handleClickMoreBtn(e: MouseEvent<HTMLAnchorElement>) {
    e.preventDefault();
    navigate('/pc/charges/fareCheck');
  }

  return (
    <DashboardCard
      title="전력 사용 요금"
      isConfigMode={props.isConfigMode ?? false}
      moreButtonVisible={true}
      moreButtonUrl="/pc/charges/fareCheck"
      dashboardConfig={props.dashboardConfig}
    >
      <div className="inner-flex1">
        <div className="chart">
          {options && (
            <HighchartsReact highcharts={Highcharts} options={chartOption} />
          )}
        </div>
      </div>
    </DashboardCard>
  );
}